import React, { useState, useEffect, useMemo } from "react";
import Slider from "react-slick";
import tw from "twin.macro";
import styled from "styled-components";
import { SectionHeading } from "components/misc/Headings";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons";
import { ReactComponent as PriceIcon } from "feather-icons/dist/icons/award.svg";
import { ReactComponent as LocationIcon } from "feather-icons/dist/icons/map-pin.svg";
import { ReactComponent as StarIcon } from "feather-icons/dist/icons/star.svg";
import { ReactComponent as ChevronLeftIcon } from "feather-icons/dist/icons/chevron-left.svg";
import { ReactComponent as ChevronRightIcon } from "feather-icons/dist/icons/chevron-right.svg";

const API_URL = process.env.REACT_APP_IS_PROD == "true" ? process.env.REACT_APP_API_URL_PROD : process.env.REACT_APP_API_URL_DEV


const Container = tw.div`relative`;
const Content = tw.div`max-w-screen-xl mx-auto py-8 lg:py-8`;

const HeadingWithControl = tw.div`flex flex-col items-center sm:items-stretch sm:flex-row justify-between`;
const Heading = tw(SectionHeading)``;
const Controls = tw.div`flex items-center`;
const ControlButton = styled(PrimaryButtonBase)`
  ${tw`mt-4 sm:mt-0 first:ml-0 ml-6 rounded-full p-2`}
  svg {
    ${tw`w-6 h-6`}
  }
`;
const PrevButton = tw(ControlButton)``;
const NextButton = tw(ControlButton)``;

const CardSlider = styled(Slider)`
  ${tw`mt-8`}
  .slick-track {
    ${tw`flex`}
  }
  .slick-slide {
    ${tw`h-auto flex justify-center mb-1`}
  }
`;
const Card = tw.div`h-full flex! flex-col sm:border max-w-sm sm:rounded-tl-4xl sm:rounded-br-5xl relative focus:outline-none`;
const CardImage = styled.div(props => [
  `background-image: url("${props.thumbnail}");`,
  tw`w-full h-40 sm:h-48 bg-cover bg-center rounded sm:rounded-none sm:rounded-tl-4xl`
]);

const TextInfo = tw.div`py-6 sm:px-10 sm:py-6`;
const TitleReviewContainer = tw.div`flex flex-col sm:flex-row sm:justify-between sm:items-center`;
const Title = tw.h5`text-xl font-bold`;

const RatingsInfo = styled.div`
  ${tw`flex items-center sm:ml-4 mt-2 sm:mt-0`}
  svg {
    ${tw`w-6 h-6 text-yellow-500 fill-current`}
  }
`;
const Rating = tw.span`ml-2 font-bold`;

const Description = tw.p`text-sm leading-loose mt-2 sm:mt-4`;

const SecondaryInfoContainer = tw.div`flex flex-col sm:flex-row mt-2 sm:mt-4`;
const IconWithText = tw.div`flex items-center mr-6 my-2 sm:my-0`;
const IconContainer = styled.div`
  ${tw`inline-block rounded-full p-2 bg-gray-700 text-gray-100`}
  svg {
    ${tw`w-3 h-3`}
  }
`;
const Text = tw.div`ml-2 text-sm font-semibold text-gray-800`;

const PrimaryButton = tw(PrimaryButtonBase)`mt-auto sm:text-lg w-full rounded sm:rounded-none sm:rounded-br-4xl sm:py-4`;

export default () => {
  const [sliderRef, setSliderRef] = useState(null);
  const [resortData, setResortData] = useState([]);
  const [whatsappNumber, setWhatsappNumber] = useState("6395977935"); // Default number

  useEffect(() => {
    // Fetch tour packages

    const fetchResorts = async () => {
      try {

        const response = await fetch(`${API_URL}/web/resorts/resort-list`, {
          headers: {
            'X-API-Key': process.env.REACT_APP_ACCESS_API_KEY // Add API key to headers
          }
        }); // API endpoint updated
        const result = await response.json();

        if (result.header.resCode === 200 && result.data.length > 0) {
          const resortData = result.data
          setResortData(resortData)
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    const fetchWhatsapp = async () => {
      try {

        const response = await fetch(`${API_URL}/web/home/wpnumber?title=whatsappNumber`, {
          headers: {
            'X-API-Key': process.env.REACT_APP_ACCESS_API_KEY // Add API key to headers
          }
        }); // API endpoint updated
        const result = await response.json();

        if (result.header.resCode === 200 && result.data.length > 0) {
          const numberData = result.data.find((item) => item.title === "whatsappNumber"); // Find the correct video by title
          if (numberData) {
            setWhatsappNumber(numberData.content);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchWhatsapp();
    fetchResorts();
  }, []);

  const handleMakeEnquiry = (hotelData) => {
    const message = `Hello, I am interested in the ${hotelData.name} Hotel/Resort. Could you provide more details?`;
    const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  const slidesToShow = useMemo(() => (resortData.length > 2 ? 3 : 1), [resortData]);

  const sliderSettings = {
    arrows: false,
    slidesToShow: slidesToShow,
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 2,
        }
      },

      {
        breakpoint: 900,
        settings: {
          slidesToShow: 1,
        }
      },
    ]
  };

  const dummyData = [
    {
      thumbnail:
        "https://images.unsplash.com/photo-1560347876-aeef00ee58a1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080",
      name: "Oceanview Retreat",
      description:
        "Nestled along the stunning Amalfi Coast, this retreat offers breathtaking ocean views, luxurious rooms, and world-class service.",
      locationText: "Amalfi,Italy",
      pricingText: "₹ 20,500/Day",
      rating: "4.9",
    },
  ];



  return (
    <>
      {resortData.length > 0 &&
        <Container>
          <Content>
            <HeadingWithControl>
              <Heading>Hotel & Resorts</Heading>
              <Controls>
                <PrevButton onClick={sliderRef?.slickPrev}><ChevronLeftIcon /></PrevButton>
                <NextButton onClick={sliderRef?.slickNext}><ChevronRightIcon /></NextButton>
              </Controls>
            </HeadingWithControl>
            <CardSlider ref={setSliderRef} {...sliderSettings}>
              {resortData.map((hotelData, index) => (
                <Card key={index}>
                  <CardImage thumbnail={hotelData.thumbnail} />
                  <TextInfo>
                    <TitleReviewContainer>
                      <Title>{hotelData.name}</Title>
                      <RatingsInfo>
                        <StarIcon />
                        <Rating>{parseInt(hotelData.rating)}</Rating>
                      </RatingsInfo>
                    </TitleReviewContainer>
                    <SecondaryInfoContainer>
                      <IconWithText>
                        <IconContainer>
                          <LocationIcon />
                        </IconContainer>
                        <Text>{hotelData.address || hotelData.locationText}</Text>
                      </IconWithText>
                      <IconWithText>
                        <IconContainer>
                          <PriceIcon />
                        </IconContainer>
                        <Text>{"₹ " + hotelData.price_per_night || "₹ " + hotelData.pricingText}</Text>
                      </IconWithText>
                    </SecondaryInfoContainer>
                    <Description>
                      {(() => {
                        let amenitiesArray;

                        try {
                          // Parse the JSON array string safely
                          amenitiesArray = JSON.parse(hotelData.amenities);
                        } catch (e) {
                          console.error("Invalid JSON format:", hotelData.amenities);
                          amenitiesArray = [];
                        }

                        // Ensure it's an array, else fallback to description
                        if (Array.isArray(amenitiesArray) && amenitiesArray.length > 0) {
                          return amenitiesArray.map((item, index) => (
                            <span key={index}>
                              {index + 1}. {item}
                              <br />
                            </span>
                          ));
                        } else {
                          return hotelData.description || "No amenities available.";
                        }
                      })()}
                    </Description>
                  </TextInfo>
                  <PrimaryButton onClick={() => handleMakeEnquiry(hotelData)}>
                    Contact Now
                  </PrimaryButton>
                </Card>
              ))}
            </CardSlider>
          </Content>
        </Container>}
    </>
  )
};
